import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Spin, Alert } from 'antd';
import { UserOutlined, PlayCircleOutlined, TrophyOutlined, DollarOutlined } from '@ant-design/icons';
import { dashboardApi } from '../services/adminApi';
import type { DashboardStats } from '../types';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const response = await dashboardApi.getStats();
        setStats(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setError('Failed to load dashboard statistics');
        // Use fallback data for demo
        setStats({
          totalUsers: 1128,
          activeGames: 12,
          totalScores: 9280,
          revenue: 25640
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2}>Dashboard</Title>
        {error && (
          <Alert
            message="Using fallback data"
            description={error}
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
      
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic
              title="Total Users"
              value={stats?.totalUsers || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic
              title="Active Games"
              value={stats?.activeGames || 0}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic
              title="Total Scores"
              value={stats?.totalScores || 0}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stats-card">
            <Statistic
              title="Revenue"
              value={stats?.revenue || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="Recent Activities" style={{ minHeight: 300 }}>
            <p>Recent user registrations, game plays, and system activities will be displayed here.</p>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Popular Games" style={{ minHeight: 300 }}>
            <p>Most played games and their statistics will be shown here.</p>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
